import React, { useEffect, useState } from "react";
import { usePrimaryOrganization } from "../../../../../hooks/useOrganization";
import { useContactVendorsMessageTemplate } from "../../../../../hooks/useTemplates";
import { useDataRequestProcessingContext } from "../../DataRequestProcessingContext";
import { VendorEmailDisplayDialog } from "../../../../dialog/VendorEmailDisplayDialog";
import { Error as RequestsError } from "../../../../../copy/requests";
import { useOrganizationVendors } from "../../../../../hooks/useOrganizationVendors";
import {
  getRequestProcessingRegion,
  isRequestContextApplicableWithCrunchFiltering,
} from "../../../../../util/vendors";
import { useDataMap } from "../../../../../../common/hooks/api/useDataMap";
import { SurveyAnswers, useSurvey } from "../../../../../../common/hooks/api/useSurvey";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../../../../../pages/get-compliant/data-recipients/shared";
import { Callout, CalloutVariant } from "../../../../Callout";
import { Link } from "react-router-dom";
import {
  CUSTOM_AUDIENCE_VENDOR_FEATURE_SLUG,
  CUSTOM_AUDIENCE_VENDOR_MANAGED_SHARING_VENDORS_FEATURE_SLUG,
  DIRECT_MAIL_VENDORS_SLUG,
} from "../../../../../surveys/sharingSurvey";
import { Routes } from "../../../../../root/routes";
import { EmailButton } from "../../../../organisms/EmailButton";
import { useApiTrigger } from "../../../../../../common/hooks/api";
import { Api } from "../../../../../../common/service/Api";
import { ContactVendorRequest } from "../../../../../../common/service/server/dto/UpdateVendorRequest";
import { OrganizationDataRecipientDto } from "../../../../../../common/service/server/dto/OrganizationDataRecipientDto";
import { DataSubjectRequestDetailDto } from "../../../../../../common/service/server/dto/DataSubjectRequestDto";
import { Tooltip } from "@mui/material";
import { parseMultiselectAnswerWithNoneOption } from "../../../../../surveys/survey";
import { DataMapDto } from "../../../../../../common/service/server/dto/datamap/DataMapDto";
import { useManagedAdNetworks } from "../../../../../../common/hooks/dataRecipientSettingsHooks";
import { Organization } from "../../../../../../common/models";

const getCompletedVendors = (
  vendors: OrganizationDataRecipientDto[],
  request: DataSubjectRequestDetailDto,
) => {
  return (
    vendors?.filter(
      (v) =>
        (v.email || v.defaultEmail) &&
        request.vendorOutcomes.find((x) => x.vendor.id === v.vendorId && x.processed),
    ) || []
  );
};

const isSuccessfulAutomatedOutcome = (vendorOutcome?: string): boolean => {
  return ["AUTOMATION_REQUESTED", "AUTOMATION_NO_DATA", "AUTOMATION_CONFIRMED"].includes(
    vendorOutcome || "",
  );
};

export const filterVendorsForOptOutNotifyThirdPartiesStep = (
  vendors: OrganizationDataRecipientDto[],
  request: DataSubjectRequestDetailDto,
  dataMap: DataMapDto,
  sellingAndSharingAnswers: SurveyAnswers,
  organization: Organization,
) => {
  // Parse the checkbox arrays properly for custom audience vendor feature
  const vendorManagedAdNetworksIds = JSON.parse(
    (sellingAndSharingAnswers
      ? sellingAndSharingAnswers[CUSTOM_AUDIENCE_VENDOR_MANAGED_SHARING_VENDORS_FEATURE_SLUG]
      : null) || "[]",
  );

  // Parse the custom audience vendor feature as a checkbox array
  const managingAdNetworksVendor = (() => {
    try {
      const rawAnswer = sellingAndSharingAnswers
        ? sellingAndSharingAnswers[CUSTOM_AUDIENCE_VENDOR_FEATURE_SLUG]
        : null;
      const parsed = JSON.parse(rawAnswer || "[]");
      return Array.isArray(parsed) ? parsed.filter((id) => id !== "none") : [];
    } catch (e) {
      return [];
    }
  })();

  // Normalize managingAdNetworksVendor to always be an array of strings, otherwise .includes fails since it uses ===
  const normalizedManagerVendors = Array.isArray(managingAdNetworksVendor)
    ? managingAdNetworksVendor.map(String)
    : managingAdNetworksVendor !== undefined && managingAdNetworksVendor !== null
    ? [String(managingAdNetworksVendor)]
    : [];

  //same thing as above
  const normalizedManagedVendors = Array.isArray(vendorManagedAdNetworksIds)
    ? vendorManagedAdNetworksIds.map(String)
    : vendorManagedAdNetworksIds !== undefined && vendorManagedAdNetworksIds !== null
    ? [String(vendorManagedAdNetworksIds)]
    : [];

  const directMailVendorIds: string[] = parseMultiselectAnswerWithNoneOption(
    sellingAndSharingAnswers,
    DIRECT_MAIL_VENDORS_SLUG,
  );

  return (vendors || []).filter((v) => {
    const optOutInstruction = v.instructions.find((vi) => vi.requestType == "OPT_OUT");
    const vendorOutcome = request.vendorOutcomes.find((x) => x.vendor.id === v.vendorId);
    return (
      (v.isUploadVendor ||
        v.usesCustomAudience ||
        normalizedManagerVendors.includes(v.id) ||
        directMailVendorIds.includes(v.id)) &&
      !normalizedManagedVendors.includes(v.id) &&
      isRequestContextApplicableWithCrunchFiltering(
        dataMap,
        request.context,
        v,
        request,
        organization,
      ) &&
      optOutInstruction?.doNotContact !== true &&
      v.processingRegions?.includes(getRequestProcessingRegion(request)) &&
      !isSuccessfulAutomatedOutcome(vendorOutcome?.outcome)
    );
  });
};

export const filterVendorsToContactForOptOutRequest = (
  optOutVendors: OrganizationDataRecipientDto[],
  request: DataSubjectRequestDetailDto,
) => {
  return optOutVendors.filter(
    (v) =>
      (v.email || v.defaultEmail) &&
      (request.vendorOutcomes.find((x) => x.vendor.id === v.vendorId && !x.processed) ||
        request.vendorOutcomes.every((x) => x.vendor.id !== v.vendorId)),
  );
};

export const NotifyThirdParties = () => {
  const [org] = usePrimaryOrganization();
  const [allVendors] = useOrganizationVendors(org.id, undefined, true);
  const { request, refreshRequest, setStepCompleted } = useDataRequestProcessingContext();

  const { answers } = useSurvey(org.id, SELLING_AND_SHARING_SURVEY_NAME);

  const { data: dataMap } = useDataMap(org.id);
  const { managedAdNetworks } = useManagedAdNetworks(org.id);

  const [updateVendorOutcome, updateVendorOutcomeRequest] = useApiTrigger(
    (values: ContactVendorRequest) => Api.dataRequest.contactVendors(org.id, request.id, values),
    refreshRequest,
    RequestsError.TransitionProcessing,
  );

  const optOutVendors = filterVendorsForOptOutNotifyThirdPartiesStep(
    allVendors,
    request,
    dataMap,
    answers,
    org,
  );
  const completedVendors = getCompletedVendors(optOutVendors, request);
  const vendorsToContact = filterVendorsToContactForOptOutRequest(optOutVendors, request);

  // Get managed ad networks IDs to filter them out of vendorsWithoutEmail
  const managedAdNetworksIds: string[] = [
    ...new Set(managedAdNetworks?.map((item) => item.networkId) || []),
  ];

  const vendorsWithoutEmail = optOutVendors.filter(
    (v) => !v.email && !v.defaultEmail && !managedAdNetworksIds.includes(v.id),
  );
  const actionableVendorIds = new Set([
    ...completedVendors.map((v) => v.vendorId),
    ...vendorsToContact.map((v) => v.vendorId),
    ...vendorsWithoutEmail.map((v) => v.vendorId),
  ]);
  const visibleOptOutVendors = optOutVendors.filter((v) => actionableVendorIds.has(v.vendorId));
  const vendorsContacted = completedVendors.length > 0;

  const [recipientsModalOpen, setRecipientsModalOpen] = useState(false);
  const [removedRecipients, setRemovedRecipients] = useState(new Set<string>());

  const [template] = useContactVendorsMessageTemplate(org.id, request.id);

  const hasValidRecipients = vendorsToContact.some((v) => !removedRecipients.has(v.vendorId));

  useEffect(() => {
    setStepCompleted(!hasValidRecipients || (vendorsContacted && vendorsWithoutEmail.length === 0));
  }, [vendorsContacted, vendorsWithoutEmail.length, hasValidRecipients]);

  const dispatchEmail = () => {
    const values = {
      vendors: vendorsToContact.map((v) => v.vendorId).filter((id) => !removedRecipients.has(id)),
    };
    updateVendorOutcome(values);
  };

  return (
    <>
      {visibleOptOutVendors.length === 0 ? (
        <p>No vendors need to be contacted for this request. You can proceed to the next step.</p>
      ) : (
        <p>
          To fulfill your data privacy obligation, the message below will be emailed to any vendors
          to inform them of the opt-out request.
        </p>
      )}

      {vendorsContacted && (
        <div className="w-640">
          <Callout variant={CalloutVariant.Green} className="mb-std">
            <p>
              Message successfully sent to{" "}
              <strong>{completedVendors.map((v) => v.name).join(", ")}.</strong>
            </p>
          </Callout>
        </div>
      )}

      {vendorsToContact.length > 0 && (
        <>
          <Callout variant={CalloutVariant.Gray} className="mb-md">
            <div className="text-t3">
              <p>
                <strong>To:</strong>{" "}
                {vendorsContacted ? (
                  <Tooltip title="You have already contacted vendors for this request">
                    <span className="color-neutral-600 text-decoration-underline">
                      See Recipients
                    </span>
                  </Tooltip>
                ) : (
                  <Link to="#" onClick={() => setRecipientsModalOpen(true)}>
                    See Recipients
                  </Link>
                )}
              </p>
              <p>
                <strong>
                  Subject: {request.dataRequestType.regulationLaw.name}{" "}
                  {request.dataRequestType.label}
                </strong>
              </p>

              <div dangerouslySetInnerHTML={{ __html: template }} />
            </div>
          </Callout>
        </>
      )}

      {vendorsWithoutEmail.length > 0 &&
        (vendorsWithoutEmail.length != optOutVendors.length ? (
          <div className="w-896 my-md">
            <Callout variant={CalloutVariant.Yellow}>
              <p>
                The notification cannot be sent to the following Data Recipients because there is no
                email address added for them:{" "}
                <strong>{vendorsWithoutEmail.map((v) => v.name).join(", ")}.</strong>
              </p>
              <p>
                {" "}
                Visit{" "}
                <a href={Routes.organization.requestHandling.requestToOptOut}>
                  Manage Request to Opt-Out Settings
                </a>{" "}
                to add a contact email.
              </p>
            </Callout>
          </div>
        ) : (
          <div className="w-896 my-md">
            <Callout variant={CalloutVariant.Yellow}>
              <p>
                The notification cannot be sent because none of your Data Recipients can be
                contacted. Add email addresses for the following Data Recipients in{" "}
                <a href={Routes.organization.requestHandling.requestToOptOut}>
                  Manage Request to Opt-Out Settings
                </a>
                , then return to this step to preview and send the notification:{" "}
                <strong>{vendorsWithoutEmail.map((v) => v.name).join(", ")}.</strong>
              </p>
            </Callout>
          </div>
        ))}

      {(vendorsToContact.length > 0 || vendorsWithoutEmail.length > 0) && (
        <EmailButton
          color="primary"
          variant="contained"
          className="btn-md mb-std"
          loading={updateVendorOutcomeRequest.running}
          onClick={dispatchEmail}
          disabled={vendorsWithoutEmail?.length > 0 || !hasValidRecipients || vendorsContacted}
          disabledTooltip={
            vendorsContacted
              ? "You have already contacted vendors for this request"
              : hasValidRecipients
              ? vendorsWithoutEmail?.length > 0
                ? "There are data recipients with no email address associated"
                : undefined
              : "You must contact at least one vendor"
          }
        >
          Send
        </EmailButton>
      )}

      <VendorEmailDisplayDialog
        open={recipientsModalOpen}
        onClose={() => setRecipientsModalOpen(false)}
        vendors={vendorsToContact}
        allowToggle={true}
        isRecipientRemoved={(v) => removedRecipients.has(v.vendorId)}
        removeRecipient={(v) => {
          setRemovedRecipients(new Set([...removedRecipients, v.vendorId]));
        }}
        addRecipient={(v) => {
          setRemovedRecipients(new Set([...removedRecipients].filter((id) => id !== v.vendorId)));
        }}
      />
    </>
  );
};
